import { useEffect, useState, useCallback, useMemo } from "react";
import { useAppSelector } from "../../../redux/store";
import {
  selectIsSolo,
  selectPlayerCharacters,
  selectGameSettings,
  selectPlayer,
} from "../../../redux/selectors";
import { useTranslation } from "react-i18next";
import { Maturity } from "../../../types";
import { PlayerAction, Character } from "../../../types";

export function numberString(num?: number) {
  switch (num) {
    case 0:
      return "zero";
    case 1:
      return "one";
    case 2:
      return "two";
    case 3:
      return "three";
    case 4:
      return "four";
    case 5:
      return "five";
    case 6:
      return "six";
    case 7:
      return "seven";
    case 8:
      return "eight";
    case 9:
      return "nine";
    case 10:
      return "ten";
    default:
      return "?";
  }
}

type CharacterFormMode = "character" | "otp";

export const useCharacterFormLogic = (mode: CharacterFormMode) => {
  const { t } = useTranslation(["game", "common"]);
  const isSolo = useAppSelector(selectIsSolo);
  const settings = useAppSelector(selectGameSettings);
  const player = useAppSelector(selectPlayer);

  // Determine the source of characters based on the mode
  const charactersSource =
    mode === "character"
      ? useAppSelector(selectPlayerCharacters)
      : player?.characters || [];

  const [characterNames, setCharacterNames] = useState(
    new Map<number, string>()
  );
  const [characterMaturities, setCharacterMaturities] = useState(
    new Map<number, boolean>()
  );
  const [singleMaturity, setSingleMaturity] = useState(false);

  // Initialize character names and maturity from the Redux state
  // Only initialize if local state is empty to prevent overwriting user input
  useEffect(() => {
    if (charactersSource.length > 0 && characterNames.size === 0) {
      const initialNames = new Map<number, string>();
      const initialMaturities = new Map<number, boolean>();

      for (const char of charactersSource) {
        if (char.name) {
          initialNames.set(char.id, char.name);
        }
        initialMaturities.set(
          char.id,
          char.meta?.mature ?? settings.maturity === Maturity.MATURE
        );
      }
      setCharacterNames(initialNames);
      setCharacterMaturities(initialMaturities);
    }
  }, [charactersSource, settings.maturity, characterNames.size]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const id = parseInt(event.target.name);
      setCharacterNames((prevState) =>
        new Map(prevState).set(id, event.target.value)
      );
      return event.target.value;
    },
    []
  );

  const handleMature = useCallback(
    (
      characterIdOrEvent: number | React.ChangeEvent<HTMLInputElement>,
      event?: React.ChangeEvent<HTMLInputElement>
    ) => {
      if (
        mode === "character" &&
        typeof characterIdOrEvent === "number" &&
        event
      ) {
        setCharacterMaturities((prevState) =>
          new Map(prevState).set(
            characterIdOrEvent,
            event.target.value === "true"
          )
        );
      } else if (mode === "otp" && typeof characterIdOrEvent === "object") {
        setSingleMaturity(characterIdOrEvent.target.value === "true");
      }
    },
    [mode]
  );

  const createPlayerAction = (): PlayerAction => {
    const chars = Array.from(characterNames).map(([id, name]) => ({
      id,
      name,
      meta: {
        mature:
          mode === "character"
            ? (characterMaturities.get(id) ??
              settings.maturity === Maturity.MATURE)
            : false, // In OTP mode, story-level maturity is used
      },
    }));

    if (mode === "character") {
      return {
        code: "game/character",
        body: { characters: chars },
      };
    }

    // OTP Mode
    return {
      code: "game/otpCharacter",
      body: {
        characters: chars,
        story: {
          id: player?.current_story?.id,
          settings: { mature: singleMaturity },
        },
      },
    };
  };

  const isAllAges = settings.maturity === Maturity.ALL_AGES;

  const subHeaders = useMemo(() => {
    const charactersToCount = charactersSource?.filter((c) => !c.meta.locked);
    const numToSubmit = numberString(charactersToCount?.length);

    if (mode === "character") {
      return [t("characterSubmit.subheaders.default", { number: numToSubmit })];
    } else {
      // OTP Mode
      const lines = [
        t("otpSubmit.subheaders.default", { number: numToSubmit }),
      ];
      if (!isSolo) {
        lines.push(t("createStory.subheaders.guide"));
        if (!isAllAges) {
          lines.push(t("createStory.subheaders.maturity"));
        }
      }
      return lines;
    }
  }, [mode, charactersSource, t, isSolo, isAllAges]);

  const submitText = useMemo(() => {
    if (mode === "character") {
      return isSolo
        ? t("button.letsGo", { ns: "common" })
        : t("characterSubmit.footers.default");
    } else {
      // OTP Mode
      return isSolo
        ? t("characterSubmit.footers.default")
        : settings.num_characters_select === 3
          ? t("otpSubmit.footers.ot3")
          : t("otpSubmit.footers.otp");
    }
  }, [mode, isSolo, t, settings.num_characters_select]);

  const titles = useMemo(() => [t("characterSubmit.headers.default")], [t]);

  return {
    // State
    characters: charactersSource,
    characterNames,
    characterMaturities,
    mature: singleMaturity,

    // Derived State & Config
    settings,
    isSolo,
    isAllAges,
    subHeaders,
    submitText,
    titles,

    // Handlers
    handleChange,
    handleMature,
    createPlayerAction,
  };
};
